import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { DateRange } from "react-day-picker";
import {
  useDailyOrderVolumeByCustomer,
  CustomerVolumeData,
} from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface Props {
  dateRange: DateRange;
}

// Define our chart data type
interface ChartDataItem {
  date: string;
  formattedDate: string;
  total: number;
  customers: Record<string, { id: string; name: string; volume: number }>;
}

// Transform data for the chart
const transformData = (
  data: CustomerVolumeData[] | undefined | null
): ChartDataItem[] => {
  // Ensure data is an array
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Group by date
  const groupedByDate = data.reduce<Record<string, ChartDataItem>>(
    (acc, curr) => {
      const dateStr = curr.date;
      if (!acc[dateStr]) {
        acc[dateStr] = {
          date: dateStr,
          formattedDate: format(parseISO(dateStr), "MMM d"),
          total: 0,
          customers: {},
        };
      }

      // Add customer volume to the total
      acc[dateStr].total += curr.volume;

      // Store individual customer data
      if (curr.customerId) {
        acc[dateStr].customers[curr.customerId] = {
          id: curr.customerId,
          name: curr.customerName,
          volume: curr.volume,
        };
      }

      return acc;
    },
    {}
  );

  // Convert to array and sort by date
  return Object.values(groupedByDate).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
};

// Get top customers by total volume
const getTopCustomers = (
  data: CustomerVolumeData[] | undefined | null,
  limit = 5
) => {
  // Ensure data is an array
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const customerVolumes: Record<
    string,
    { id: string; name: string; volume: number }
  > = {};

  // Sum up volumes by customer
  data.forEach((item) => {
    if (item?.customerId) {
      if (!customerVolumes[item.customerId]) {
        customerVolumes[item.customerId] = {
          id: item.customerId,
          name: item.customerName,
          volume: 0,
        };
      }
      customerVolumes[item.customerId].volume += item.volume;
    }
  });

  // Sort by volume (descending) and take top N
  return Object.values(customerVolumes)
    .sort((a, b) => b.volume - a.volume)
    .slice(0, limit);
};

// Custom tooltip component
const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    return (
      <Card className="p-2 bg-white shadow-md border">
        <CardContent className="p-2">
          <p className="font-bold">{label}</p>
          <div className="flex justify-between">
            <span>Total: </span>
            <span className="font-semibold ml-2">
              {payload[0]?.value} orders
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }
  return null;
};

export default function DailyOrderVolumeByCustomer({ dateRange }: Props) {
  // Use the API hook
  const { data, isLoading, error } = useDailyOrderVolumeByCustomer(dateRange);

  // Transform data for chart
  const chartData = useMemo(() => transformData(data), [data]);

  // Get top customers for additional display
  const topCustomers = useMemo(() => getTopCustomers(data), [data]);

  if (isLoading) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center">
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  if (error) {
    console.error("Error fetching customer volume data:", error);
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-red-500">
        Failed to fetch data. Please try again.
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-gray-500">
        No data available for the selected date range
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="w-full h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="formattedDate" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#4f46e5"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name="Total Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {topCustomers.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-2">Top Customers</h3>
          <div className="grid grid-cols-1 gap-2">
            {topCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex justify-between items-center p-2 bg-muted rounded"
              >
                <span className="text-sm">{customer.name}</span>
                <span className="text-sm font-semibold">
                  {customer.volume} orders
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
